'use client';

import { Button, Input, Snackbar, TooltipContent, TooltipTolq, Typography } from 'components';
import { Member, RoleEnum } from 'gql/graphql';
import { toaster } from 'helpers/toaster';
import { BinIcon, InfoHollowIcon } from 'lib/svgs/Icons';
import { useTranslations } from 'next-intl';
import { useRouter } from 'next/navigation';
import { useClientAdmin } from 'providers/UserProvider/useClientAdmin';
import { useCurrentUser } from 'providers/UserProvider/useCurrentUser';
import { useInviteMember } from 'providers/UserProvider/useInviteMember';
import { useMemberInviteResend } from 'providers/UserProvider/useMemberInviteResend';
import { useMemberRemove } from 'providers/UserProvider/useMemberRemove';
import { useMembers } from 'providers/UserProvider/useMembers';
import { ChangeEvent, useEffect, useState } from 'react';
import { notifyException } from 'services/appsignal';
import { ESpansException } from 'types/ESpans';
import { TScheme } from 'types/TScheme';
import { cn } from 'utils/cn';
import { timeAgo } from 'utils/time/timeAgo';
import * as yup from 'yup';
import ModalDeleteMemberAccount from './ModalDeleteMemberAccount';

export default function ManagePage() {
  const [memberEmail, setMemberEmail] = useState('');
  const [resendMemberEmail, setResendMemberEmail] = useState('');
  const [deleteMemberUser, setDeleteMemberUser] = useState<Member | null>(null);

  const router = useRouter();

  const t = useTranslations();
  const { data: currentUser } = useCurrentUser();
  const { data: clientAdmin, isFetched } = useClientAdmin();
  const { data: members, refetch } = useMembers();

  const variables = {
    email: memberEmail || resendMemberEmail
  };

  const variablesRemove = {
    email:
      members?.find((member) => deleteMemberUser && member?.id === deleteMemberUser?.id)?.email ??
      ''
  };
  const schema = yup.object().shape({
    email: yup
      .string()
      .nullable()
      .email(t('messages.fields.email_validation'))
      .required(t('messages.fields.email_required'))
  } as TScheme<{ email: string }>);

  const { mutateAsync: inviteMember, isPending: isMemberInvitePending } = useInviteMember({
    onSuccess: () => toaster.success(t('account_details.editors.invitation_success', variables)),
    onError: (error: unknown) => {
      notifyException(error, ESpansException.AccountDetailsException);
      toaster.error(t('account_details.editors.invitation_error', variables));
    }
  });

  const { mutateAsync: memberInviteResend } = useMemberInviteResend({
    onSuccess: () => toaster.success(t('account_details.editors.invitation_success', variables)),
    onError: (error: unknown) => {
      notifyException(error, ESpansException.AccountDetailsException);
      toaster.error(t('account_details.editors.invitation_error', variables));
    }
  });

  const { mutateAsync: removeMember } = useMemberRemove({
    onSuccess: () => toaster.success(t('account_details.editors.delete_success', variablesRemove)),
    onError: (error: unknown) => {
      notifyException(error, ESpansException.AccountDetailsException);
      toaster.error(t('account_details.editors.delete_error', variablesRemove));
    }
  });

  const onRemoveMember = async () => {
    await removeMember({
      memberUserId: deleteMemberUser?.id as string
    });
    refetch();
    setDeleteMemberUser(null);
  };

  const onChange = (e: ChangeEvent<HTMLInputElement>) => setMemberEmail(e.target.value.trim());

  const onInvite = async () => {
    try {
      await schema.validate(variables);
    } catch (error) {
      return toaster.warning((error as yup.ValidationError)?.message);
    }
    await inviteMember(variables);
    refetch();
    setMemberEmail('');
  };

  const onResendInvite = (email?: string, memberUserId?: string) => async () => {
    if (email) {
      setMemberEmail('');
      setResendMemberEmail(email);
      await memberInviteResend({
        memberUserId: memberUserId as string
      });
      setMemberEmail('');
    }
  };

  const onTryRemoveMember = (member?: Member) => () => setDeleteMemberUser(member as Member);

  const onCancelRemoveMember = () => {
    setDeleteMemberUser(null);
    refetch();
  };

  useEffect(() => {
    if (isFetched && !clientAdmin?.allowedToHaveEditors) {
      router.push('/account-details');
    }
  }, [clientAdmin?.allowedToHaveEditors, isFetched, router]);

  if (!isFetched || !clientAdmin?.allowedToHaveEditors) {
    return null;
  }

  return (
    <>
      <div className="py-8">
        <Snackbar
          className="mb-8 border border-solid border-tertiary-400"
          description={
            <span dangerouslySetInnerHTML={{ __html: t.raw('account_details.editors.intro') }} />
          }
        />

        <div className="w-full rounded-md bg-white py-4 shadow-custom">
          <div className="flex justify-between px-4">
            <div className="flex shrink-0">
              <Typography variant="regular1" className="!mt-0 flex text-tertiary-100">
                {t('account_details.editors.team_members')}
                <span className="ml-2 text-tertiary-200">
                  {' ('}
                  {members?.length ?? 0}
                  {')'}
                </span>
              </Typography>
              <TooltipTolq
                placement={'auto-end'}
                className="!-top-12"
                label={
                  <TooltipContent>
                    {t('account_details.editors.email_input_explanation')}{' '}
                  </TooltipContent>
                }
              >
                <InfoHollowIcon
                  className={cn(
                    'absolute top-[-1px] scale-[0.55] cursor-pointer transition-all duration-300 hover:scale-[0.6]'
                  )}
                />
              </TooltipTolq>
            </div>
            <div className="flex flex-1 items-center justify-end gap-4">
              <div className="w-4/12">
                <Input
                  type="text"
                  name="email"
                  value={memberEmail || ''}
                  placeholder={t('account_details.editors.placeholder')}
                  onChange={onChange}
                />
              </div>
              <Button
                size="medium"
                label={t('account_details.editors.create_team_member')}
                onClick={onInvite}
                disabled={isMemberInvitePending}
              />
            </div>
          </div>
          {!!members?.length && (
            <table className="w-100 bg-almost-white mt-4 table w-full">
              <thead className="bg-tertiary-900">
                <tr className="h-12 border-0 border-b-1 border-solid border-tertiary-600">
                  <th>
                    <Typography
                      variant="regular4"
                      className="block w-full pl-4 text-left text-tertiary-200"
                    >
                      {t('account_details.editors.email')}
                    </Typography>
                  </th>
                  <th>
                    <Typography
                      variant="regular4"
                      className="block w-full pl-4 text-left text-tertiary-200"
                    >
                      {t('account_details.editors.last_sign_in')}
                    </Typography>
                  </th>
                  <th>
                    <Typography
                      variant="regular4"
                      className="block w-full pl-4 text-left text-tertiary-200"
                    >
                      {t('account_details.editors.translation_requests')}
                    </Typography>
                  </th>
                  <th>
                    <Typography
                      variant="regular4"
                      className="block w-full pl-4 text-left text-tertiary-200"
                    >
                      {t('account_details.editors.account_type')}
                    </Typography>
                  </th>
                  <th></th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {members?.map((member, index) => (
                  <tr
                    key={member?.id}
                    className="h-12 border-0 border-solid border-tertiary-600 [&:not(:last-child)]:border-b-1"
                  >
                    <td>
                      <Typography
                        variant="light4"
                        className="block w-full pl-4 text-left text-tertiary-100"
                      >
                        {member?.email}
                      </Typography>
                    </td>
                    <td>
                      <Typography
                        variant="light4"
                        className="block w-full pl-4 text-left text-tertiary-100"
                      >
                        {!member?.lastSignInAt
                          ? t('account_details.editors.never')
                          : timeAgo(member?.lastSignInAt, t)}
                      </Typography>
                    </td>
                    <td>
                      <Typography
                        variant="light4"
                        className="block w-full pl-4 text-left text-tertiary-100"
                      >
                        {member?.translationRequestCount ?? 0}
                      </Typography>
                    </td>
                    <td>
                      <Typography
                        variant="light4"
                        className="block w-full pl-4 text-left text-tertiary-100"
                      >
                        {member?.role === RoleEnum.Manager
                          ? t('account_details.editors.manager')
                          : t('account_details.editors.editor')}
                      </Typography>
                    </td>
                    <td className="w-fit">
                      <div className="flex w-full justify-center">
                        {!member?.lastSignInAt && (
                          <Button
                            size="small"
                            variant="text"
                            label={t('account_details.editors.resend_invitation')}
                            onClick={onResendInvite(member?.email, member?.id)}
                          />
                        )}
                      </div>
                    </td>
                    <td>
                      {index !== 0 && (
                        <Button
                          size="small"
                          variant="text"
                          colorIcon="danger"
                          startAdornment={<BinIcon className="mb-[2px] !text-secondary-red" />}
                          className="!text-secondary-red"
                          label={t('account_details.editors.delete_editor')}
                          onClick={onTryRemoveMember(member as Member)}
                        />
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
      <ModalDeleteMemberAccount
        onCancel={onCancelRemoveMember}
        open={!!deleteMemberUser?.id}
        member={deleteMemberUser}
      />
    </>
  );
}
